import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LinkIcon, TagIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import ErrorMessage from '../components/ErrorMessage';
import HelpTooltip from '../components/HelpTooltip';
import { bookmarkService } from '../services/bookmarkService';

const AddBookmark = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    url: '',
    title: '',
    description: '',
    tags: '',
    isPublic: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [duplicateId, setDuplicateId] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const normalizeUrl = (raw) => {
    const trimmed = raw.trim();
    if (!trimmed) return trimmed;
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    return `https://${trimmed}`;
  };

  const validateUrl = (url) => {
    try {
      const parsed = new URL(url);
      return ['http:', 'https:'].includes(parsed.protocol);
    } catch {
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setDuplicateId(null);

    // Validation
    if (!formData.url.trim()) {
      setError('URL is required');
      return;
    }

    const normalizedUrl = normalizeUrl(formData.url);

    if (!validateUrl(normalizedUrl)) {
      setError('Please enter a valid URL');
      return;
    }

    try {
      setLoading(true);

      // Process tags
      const tags = formData.tags
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0);

      const bookmarkData = {
        url: normalizedUrl,
        title: formData.title.trim(),
        description: formData.description.trim(),
        tags,
        isPublic: formData.isPublic
      };

      await bookmarkService.createBookmark(bookmarkData);
      
      // Redirect to bookmarks list
      navigate('/bookmarks');
    } catch (error) {
      if (error.code === 'duplicate_url') {
        setDuplicateId(error.existingId || null);
        setError('duplicate_url');
      } else {
        console.error('Error creating bookmark:', error);
        setError('Failed to create bookmark. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">Add New Bookmark</h1>
          <p className="text-gray-600 dark:text-gray-400">
            Save a new URL to your bookmark collection. We'll automatically analyze the content and generate tags and summaries.
          </p>
        </div>

        {error === 'duplicate_url' ? (
          <div className="mb-6 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-md p-4 flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-amber-800 dark:text-amber-400">You've already saved this URL.</p>
              {duplicateId && (
                <button
                  type="button"
                  onClick={() => navigate('/bookmarks', { state: { highlightId: duplicateId } })}
                  className="text-sm text-amber-700 dark:text-amber-400 underline mt-1 hover:text-amber-900"
                >
                  View existing bookmark →
                </button>
              )}
            </div>
            <button type="button" onClick={() => { setError(''); setDuplicateId(null); }} className="text-amber-500 hover:text-amber-700 ml-4 text-lg leading-none">×</button>
          </div>
        ) : error ? (
          <ErrorMessage
            message={error}
            onRetry={() => setError('')}
          />
        ) : null}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* URL Field */}
          <div>
            <label htmlFor="url" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <LinkIcon className="inline h-4 w-4 mr-1" />
              URL *
            </label>
            <input
              type="text"
              id="url"
              name="url"
              value={formData.url}
              onChange={handleInputChange}
              placeholder="example.com or https://example.com"
              className="form-input"
              required
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              If no protocol is provided, https:// will be used automatically.
            </p>
          </div>

          {/* Title Field */}
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Title (Optional)
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="Custom title for this bookmark"
              className="form-input"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Leave blank to use the page's title automatically
            </p>
          </div>

          {/* Description Field */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <DocumentTextIcon className="inline h-4 w-4 mr-1" />
              Description (Optional)
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Add a personal note or description"
              rows={3}
              className="form-input resize-none"
            />
          </div>

          {/* Tags Field */}
          <div>
            <label htmlFor="tags" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <TagIcon className="inline h-4 w-4 mr-1" />
              Tags (Optional)
              <HelpTooltip id="bookmark-tags" className="ml-1 align-middle" />
            </label>
            <input
              type="text"
              id="tags"
              name="tags"
              value={formData.tags}
              onChange={handleInputChange}
              placeholder="web development, react, tutorial"
              className="form-input"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Separate tags with commas. Additional tags will be generated automatically.
            </p>
          </div>

          {/* Submit Buttons */}
          <div className="flex justify-end space-x-4 pt-6">
            <button
              type="button"
              onClick={() => navigate('/bookmarks')}
              className="btn-secondary"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary flex items-center space-x-2"
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Creating...</span>
                </>
              ) : (
                <>
                  <LinkIcon className="h-4 w-4" />
                  <span>Add Bookmark</span>
                </>
              )}
            </button>
          </div>
        </form>

        {/* Info Box */}
        <div className="mt-8 bg-primary-50 dark:bg-primary-900/30 border border-primary-200 dark:border-primary-700 rounded-md p-4">
          <h3 className="text-sm font-medium text-primary-900 dark:text-primary-100 mb-2">What happens next?</h3>
          <ul className="text-sm text-primary-800 dark:text-primary-200 space-y-1">
            <li>• We'll fetch and analyze the webpage content</li>
            <li>• Generate relevant tags and categories automatically</li>
            <li>• Create a summary of the page content</li>
            <li>• Make it searchable in your bookmark collection</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AddBookmark;
